import { serializeMaipoPublicScene } from './canonical';
import { decodeMaipoJsonBytes } from './json';
import { validatePortableTextLayout, type PortableTextLayoutPayload } from './portable-text-contract';
import { parseMaipoPublicScene, publicSceneDiagnostics, sceneSupportDisposition, verifyPublicAssets } from './validation';
import type { MaipoPublicSceneV01, PublicGroupNode, PublicNode, PublicTextNode } from './types';

/** Unpublished rendering semantics; never admitted by the 0.1 scene reader. */
export const PORTABLE_SCENE_VERSION = '0.2-draft' as const;
export const PORTABLE_SCENE_SCHEMA = 'urn:maipo:scene:0.2-draft' as const;
export interface PortableSceneTextNode extends Omit<PublicTextNode, 'type'> {
  type: 'portable-text';
  layout: PortableTextLayoutPayload;
}
export type PortableSceneNode = Exclude<PublicNode, PublicGroupNode> | PortableSceneTextNode
  | (Omit<PublicGroupNode, 'children'> & { children: PortableSceneNode[] });
export interface PortableScene extends Omit<MaipoPublicSceneV01, '$schema' | 'version' | 'children'> {
  $schema?: typeof PORTABLE_SCENE_SCHEMA;
  version: typeof PORTABLE_SCENE_VERSION;
  children: PortableSceneNode[];
}

/** Only a structural validation view. It must never be rendered or exported as 0.1. */
function structuralScene(value: unknown): { base: MaipoPublicSceneV01; layouts: Map<string, PortableTextLayoutPayload> } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Portable scene must be an object.');
  const input = value as Record<string, unknown>;
  if (input.format !== 'maipo-scene' || input.version !== PORTABLE_SCENE_VERSION
    || (input.$schema !== undefined && input.$schema !== PORTABLE_SCENE_SCHEMA)) throw new Error('Unsupported portable scene version or schema.');
  const layouts = new Map<string, PortableTextLayoutPayload>();
  const copy = structuredClone(input);
  const visit = (value: unknown, depth: number): unknown => {
    if (depth > 64) throw new Error('Portable scene exceeds the nesting limit.');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
    const node = value as Record<string, unknown>;
    if (node.type === 'group' && Array.isArray(node.children)) node.children = node.children.map(child => visit(child, depth + 1));
    if (node.type !== 'portable-text') return node;
    if (typeof node.id !== 'string' || layouts.has(node.id)) throw new Error('Portable text identity is missing or repeated.');
    layouts.set(node.id, node.layout as PortableTextLayoutPayload);
    delete node.layout; node.type = 'text'; return node;
  };
  if (Array.isArray(copy.children)) copy.children = copy.children.map(node => visit(node, 0));
  delete copy.$schema; copy.version = '0.1';
  const base = parseMaipoPublicScene(copy);
  const check = (node: PublicNode): void => {
    if (node.type === 'group') { node.children.forEach(check); return; }
    if (!layouts.has(node.id)) return;
    if (node.type !== 'text') throw new Error('Portable layout must belong to text.');
    const layout = layouts.get(node.id)!;
    validatePortableTextLayout(layout, { value: node.text,
      box: { autoHeight: node.frame.autoHeight ?? false },
      style: { lineHeight: node.style.lineHeight, tracking: node.style.letterSpacing, align: node.style.textAlign } });
    if (node.style.fontRef !== layout.fonts[0]) throw new Error('Portable primary font must match style.fontRef.');
    for (const ref of layout.fonts) {
      const font = base.fonts.find(font => font.id === ref);
      if (!font || font.source.kind !== 'embedded' || font.style !== 'normal') throw new Error('Portable text requires embedded normal-style font candidates.');
      const assetId = font.source.asset;
      const asset = base.assets.find(asset => asset.id === assetId);
      if (!asset || !asset.mediaType.startsWith('font/')) throw new Error('Portable text font bytes are missing.');
    }
  };
  base.children.forEach(check);
  return { base, layouts };
}

function restore(base: MaipoPublicSceneV01, layouts: Map<string, PortableTextLayoutPayload>): PortableScene {
  const visit = (node: PublicNode): PortableSceneNode => {
    if (node.type === 'group') return { ...node, children: node.children.map(visit) };
    const layout = layouts.get(node.id);
    if (node.type !== 'text' || !layout) return node;
    return { ...node, type: 'portable-text', layout: {
      version: layout.version, revision: layout.revision, fonts: [...layout.fonts], baseDirection: layout.baseDirection,
      language: layout.language, scriptLanguages: Object.fromEntries(Object.entries(layout.scriptLanguages).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)),
      metricsPolicy: layout.metricsPolicy, lineGap: layout.lineGap, overflow: layout.overflow, snapshot: layout.snapshot,
    } };
  };
  return { ...base, $schema: PORTABLE_SCENE_SCHEMA, version: PORTABLE_SCENE_VERSION, children: base.children.map(visit) };
}

/** Structural/resource-reference validation only; rendering requires recomputed layout admission. */
export function parsePortableScene(value: unknown): PortableScene {
  const { base, layouts } = structuralScene(value);
  return restore(base, layouts);
}

export function decodePortableScene(bytes: Uint8Array): PortableScene {
  return parsePortableScene(decodeMaipoJsonBytes(bytes, { maxBytes: 64 * 1024 * 1024, maxCodeUnits: 64 * 1024 * 1024 }));
}

/** Dedicated draft canonical route. Published scene canonicalization stays unchanged. */
export function serializePortableScene(value: unknown): string {
  const { base, layouts } = structuralScene(value);
  const canonical = JSON.parse(serializeMaipoPublicScene(base)) as MaipoPublicSceneV01;
  return JSON.stringify(restore(canonical, layouts), null, 2) + '\n';
}

/** Hash checks are distinct from structural parsing and from layout admission. */
export async function verifyPortableSceneAssets(value: unknown) {
  return verifyPublicAssets(structuralScene(value).base);
}

/** Legacy text inside a mixed draft retains its existing portability warnings. */
export function portableSceneDiagnostics(value: unknown) {
  return publicSceneDiagnostics(structuralScene(value).base);
}

/** A parse/round-trip is not permission to interpret required extensions. */
export function portableSceneSupportDisposition(value: unknown) {
  return sceneSupportDisposition(structuralScene(value).base);
}
